import React, { useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { getComponent } from '@syncfusion/ej2-base';
import { DropDownList, MultiSelect } from '@syncfusion/ej2-react-dropdowns';
import { QueryBuilderComponent } from "@syncfusion/ej2-react-querybuilder";

function parseFields(fields) {
    if (!fields) return {};
    if (typeof fields === "string") {
        try {
            const obj = JSON.parse(fields);
            return obj && typeof obj === "object" ? obj : {};
        } catch {
            return {};
        }
    }
    if (typeof fields === "object") return fields;
    return {};
}

/** Utility: infer Syncfusion column type from a JS value */
function inferType(val) {
    if (val instanceof Date) return "date";
    switch (typeof val) {
        case "number":
            return "number";
        case "boolean":
            return "boolean";
        case "string": {
            const d = new Date(val);
            if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(String(val))) {
                return "date";
            }
            return "string";
        }
        default:
            return "string";
    }
}

/** Helper: pretty labels for operators */
function opLabel(k) {
    const map = {
        equal: "Equal",
        notequal: "Not Equal",
        greaterthan: "Greater Than",
        greaterthanorequal: "Greater Than Or Equal",
        lessthan: "Less Than",
        lessthanorequal: "Less Than Or Equal",
        // contains: "Contains",
        // startswith: "Starts With",
        // endswith: "Ends With",
        // isnull: "Is Null",
        // isnotnull: "Is Not Null",
        // between: "Between",
        // in: "In",
        // notin: "Not In",
    };
    return map[k] || k;
}

function opList(keys) {
    // return keys.map((k) => ({ key: k, value: opLabel(k) }));
    return keys.map((k) => ({ key: opLabel(k), value: k }));
}

/** Build Syncfusion columns from all items' fields using <name>.<field> */
function buildColumns(items, qryBldrObj) {
    const columns = [];
    const rightSide = items.map(i => {
        const obj = parseFields(i.fields);
        const keys = Object.keys(obj);
        return keys.map(k => `${i.name}.${k}`);
    }).flat();
    for (const item of items) {
        const obj = parseFields(item.fields);
        for (const [k, v] of Object.entries(obj)) {
            const type = inferType(v);
            const typeOps = {
                string: opList([
                    "equal",
                    "notequal",
                    "greaterthan",
                    "greaterthanorequal",
                    "lessthan",
                    "lessthanorequal",
                    // "contains",
                    // "startswith",
                    // "endswith",
                    // "isnull",
                    // "isnotnull",
                    // "in",
                    // "notin",
                ]),
                number: opList([
                    "equal",
                    "notequal",
                    "greaterthan",
                    "greaterthanorequal",
                    "lessthan",
                    "lessthanorequal",
                    // "between",
                    // "isnull",
                    // "isnotnull",
                    // "in",
                    // "notin",
                ]),
                boolean: opList([
                    "equal",
                    "notequal",
                    "greaterthan",
                    "greaterthanorequal",
                    "lessthan",
                    "lessthanorequal",
                    //"isnull", "isnotnull"
                ]),
                date: opList([
                    "equal",
                    "notequal",
                    "greaterthan",
                    "greaterthanorequal",
                    "lessthan",
                    "lessthanorequal",
                    // "between",
                    // "isnull",
                    // "isnotnull",
                ]),
            };

            const ops = normalizeOperators(typeOps[type]);
            let elem;
            let dropDownObj;
            let multiSelectObj;

            const template = {
                create: () => {
                    elem = document.createElement('input');
                    elem.setAttribute('type', 'text');
                    return elem;
                },
                destroy: (args) => {
                    multiSelectObj = getComponent(document.getElementById(args.elementId), 'multiselect');
                    if (multiSelectObj) {
                        multiSelectObj.destroy();
                    }
                    dropDownObj = getComponent(document.getElementById(args.elementId), 'dropdownlist');
                    if (dropDownObj) {
                        dropDownObj.destroy();
                    }
                },
                write: (args) => {
                    const ds = rightSide;
                    dropDownObj = new DropDownList({
                        change: (e) => {
                            qryBldrObj.current.notifyChange(e.itemData.value, e.element);
                        },
                        dataSource: ds,
                        value: args.values ? args.values : null,
                    });
                    dropDownObj.appendTo('#' + args.elements.id);
                }
            }

            columns.push({
                field: `${item.name}.${k}`,
                label: `${item.name}.${k}`,
                type,
                operators: ops,
                template: template,
            });
        }
    }
    // Deduplicate
    const uniq = new Map();
    for (const c of columns) uniq.set(c.field, c);
    return Array.from(uniq.values());
}

/** Normalize any operator array to always be objects {key, value}. */
function normalizeOperators(list) {
    if (!Array.isArray(list)) return [];
    return list.map((op) => {
        if (op && typeof op === "object" && "value" in op) return op; // already OK
        if (typeof op === "string") return { key: opLabel(op), value: op };
        // fallback
        const s = String(op ?? "");
        return { key: opLabel(s), value: s };
    });
}

/** Operator → expression mapping */
const OP = {
    equal: (f, v) => { const vv = Array.isArray(v) ? v[0] : v; return `${f} == ${formatValue(vv)}`; },
    notequal: (f, v) => { const vv = Array.isArray(v) ? v[0] : v; return `${f} != ${formatValue(vv)}`; },
    greaterthan: (f, v) => `${f} > ${formatValue(Array.isArray(v) ? v[0] : v)}`,
    greaterthanorequal: (f, v) => `${f} >= ${formatValue(Array.isArray(v) ? v[0] : v)}`,
    lessthan: (f, v) => `${f} < ${formatValue(Array.isArray(v) ? v[0] : v)}`,
    lessthanorequal: (f, v) => `${f} <= ${formatValue(Array.isArray(v) ? v[0] : v)}`,
    contains: (f, v) => `contains(${f}, ${formatValue(Array.isArray(v) ? v[0] : v)})`,
    notcontains: (f, v) => `!contains(${f}, ${formatValue(Array.isArray(v) ? v[0] : v)})`,
    startswith: (f, v) => `startswith(${f}, ${formatValue(Array.isArray(v) ? v[0] : v)})`,
    endswith: (f, v) => `endswith(${f}, ${formatValue(Array.isArray(v) ? v[0] : v)})`,
    isnull: (f) => `${f} == null`,
    isnotnull: (f) => `${f} != null`,
    between: (f, v) => {
        const [a, b] = Array.isArray(v) ? v : [undefined, undefined];
        return `(${f} >= ${formatValue(a)} and ${f} <= ${formatValue(b)})`;
    },
    notbetween: (f, v) => {
        const [a, b] = Array.isArray(v) ? v : [undefined, undefined];
        return `(${f} < ${formatValue(a)} or ${f} > ${formatValue(b)})`;
    },
    in: (f, v) => {
        const arr = Array.isArray(v) ? v : [v];
        return `(${arr.map((x) => `${f} == ${formatValue(x)}`).join(" || ")})`;
    },
    notin: (f, v) => {
        const arr = Array.isArray(v) ? v : [v];
        return `(${arr.map((x) => `${f} != ${formatValue(x)}`).join(" && ")})`;
    },
    isempty: (f) => `${f} == ""`,
    isnotempty: (f) => `${f} != ""`,
};

/** Stringify values into a RulesEngine-friendly literal */
function formatValue(v) {
    if (v === null || v === undefined) return "null";
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return `"${v.toISOString()}"`;
    if (typeof v === "string") {
        return String(v);
        // const s = v.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        // return `"${s}"`;
    }
    if (Array.isArray(v)) return `[${v.map(formatValue).join(", ")}]`;
    return JSON.stringify(v);
}

/** Convert Syncfusion rules JSON → expression string (recursive) */
function normalizeRuleOperator(op) {
    let s = "";
    if (!op) return "";
    if (typeof op === "string") s = op;
    else if (typeof op === "object") s = (op.value ?? op.key ?? "");
    s = String(s).toLowerCase();
    s = s.split(" ").join("").split("-").join("").split("_").join("");
    if (s === "doesnotcontain") s = "notcontains";
    if (s === "notequals") s = "notequal";
    if (s === "greaterthanequal") s = "greaterthanorequal";
    if (s === "lessthanequal") s = "lessthanorequal";
    return s;
}

function rulesToExpression(model) {
    if (!model) return "";

    // leaf rule
    if (model.field && model.operator !== undefined) {
        const field = model.field;
        const opKey = normalizeRuleOperator(model.operator);
        const opFn = OP[opKey];
        const value = model.value;
        if (!opFn) return "";
        return opFn(field, value);
    }

    // group
    const condition = (model.condition || "and").toLowerCase() === "or" ? "or" : "and";
    const joiner = condition === "and" ? " AND " : " OR ";
    const parts = [];
    const children = model.rules || model.rule || [];
    for (const r of children) {
        const part = rulesToExpression(r);
        if (part) parts.push(part);
    }
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    return `(${parts.join(joiner)})`;
}

export default function RulesQueryBuilder({ items, onChange, readonly = false, rule = null }) {
    const qbRef = useRef(null);
    const columns = useMemo(() => buildColumns(items, qbRef), [items]);

    const initialRules = useMemo(() => {
        const first = columns[0];
        return first
            ? {
                condition: "and",
                rules: [
                    {
                        field: first.field,
                        label: first.label,
                        type: first.type,
                        operator: first.type === "string" ? "contains" : "equal",
                        value: first.type === "string" ? "" : 0,
                    },
                ],
            }
            : { condition: "and", rules: [] };
    }, [columns]);

    function onQBAnyChange() {
        if (!qbRef.current) return;
        const rules = qbRef.current.getRules();
        onChange(rulesToExpression(rules), rules);
    }

    return (
        <Box sx={{ p: 3, position: "relative" }}>
            <Box sx={{ position: "absolute", top: 0, left: 0, backgroundColor: "rgba(255,255,255,0.4)", width: "100%", height: "100%", display: readonly ? "block" : "none", zIndex: 10 }} aria-hidden="true">
            </Box>
            <QueryBuilderComponent
                ref={(scope) => {
                    qbRef.current = scope;
                }}
                disabled
                width="100%"
                columns={columns}
                allowDragAndDrop={!readonly}
                rule={rule || initialRules}
                immediateModeDelay={300}
                change={onQBAnyChange}
                ruleChange={onQBAnyChange}
                readOnly={readonly}
                actionBegin={(e) => {
                    const t = e?.requestType;
                    if (t && /rule|group/.test(t)) onQBAnyChange();
                }}
            />
        </Box>
    );
}
